import { z } from 'zod';
import { alertConditionSchema } from '../alerts/schemas';
// Alerts: email (or Slack) notifications when a call or chat matches conditions. Via manages email recipients;
// Slack channels are picked in the app, and Via leaves an alert's Slack channels as they are.
/** How many of an alert's latest events get_alert reads. */
export const ALERT_EVENTS_SHOWN = 10;
const HOUR_MS = 60 * 60 * 1000;
const conditionGroupsSchema = z
    .array(z.array(alertConditionSchema).min(1))
    .min(1)
    .describe('Groups of conditions. The alert fires when every condition in any one group matches: AND within a group, OR across groups.');
const alertSummarySchema = z.object({
    alertId: z.string(),
    name: z.string(),
    description: z.string().optional(),
    state: z.enum(['on', 'off', 'snoozed']),
    snoozedUntil: z.string().optional(),
    emailRecipients: z.array(z.string()),
    slackChannels: z.number().describe('How many Slack channels it posts to'),
    lastFiredAt: z.string().optional(),
    firedLast24Hours: z.number(),
    firedTotal: z.number()
});
export const listAlertsToolSchema = {
    name: 'list_alerts',
    description: "List the organization's alerts: whether each is on, off or snoozed, who it notifies, and how often it has fired.",
    inputSchema: z.object({}),
    outputSchema: z.object({ alerts: z.array(alertSummarySchema) })
};
export const getAlertToolSchema = {
    name: 'get_alert',
    description: "Read an alert's conditions and recipients, and the conversations it fired on most recently.",
    inputSchema: z.object({ alertId: z.string() }),
    outputSchema: alertSummarySchema.extend({
        conditions: z.array(z.array(z.record(z.string(), z.unknown()))),
        emailSubject: z.string().optional(),
        recentEvents: z.array(z.object({
            firedAt: z.string(),
            conversationId: z.string().optional(),
            kind: z.enum(['call', 'chat']).optional(),
            delivery: z.string().describe('Whether the email and Slack notifications were sent')
        }))
    })
};
export const createAlertToolSchema = {
    name: 'create_alert',
    description: 'Create an alert. It is on as soon as it exists and emails the recipients whenever a call or chat matches its conditions.',
    inputSchema: z.object({
        name: z.string().min(1).max(255),
        description: z.string().max(1000).optional(),
        conditions: conditionGroupsSchema,
        emailRecipients: z
            .array(z.string().email())
            .describe('Can be empty, for an alert that only posts to Slack once the user adds a channel in the app'),
        emailSubject: z.string().max(500).optional().describe('Leave out for the default subject')
    }),
    outputSchema: z.object({ alertId: z.string(), name: z.string() })
};
export const updateAlertToolSchema = {
    name: 'update_alert',
    description: "Change an alert: its name, description, conditions, email recipients or subject, turn it on or off, or snooze it. Leave out what shouldn't change. Its Slack channels stay as they are.",
    inputSchema: z.object({
        alertId: z.string(),
        name: z.string().min(1).max(255).optional(),
        description: z.string().max(1000).optional(),
        conditions: conditionGroupsSchema.optional(),
        emailRecipients: z
            .array(z.string().email())
            .optional()
            .describe('The full new list, replacing the old one'),
        emailSubject: z.string().max(500).optional(),
        on: z.boolean().optional().describe('Turn the alert on or off. Turning it on also ends a snooze.'),
        snoozeHours: z
            .number()
            .min(0)
            .max(168)
            .optional()
            .describe('Pause notifications for this many hours; 0 ends a snooze')
    }),
    outputSchema: z.object({ alertId: z.string() })
};
export const deleteAlertToolSchema = {
    name: 'delete_alert',
    description: 'Delete an alert and its history. This cannot be undone.',
    inputSchema: z.object({ alertId: z.string() }),
    outputSchema: z.object({ alertId: z.string(), name: z.string() })
};
const isoTime = (ms) => new Date(ms).toISOString();
const alertState = ({ enabledAt, snoozedUntil }, now) => {
    if (enabledAt === null)
        return 'off';
    return snoozedUntil && snoozedUntil > now ? 'snoozed' : 'on';
};
export const summarizeAlert = (alert, now = Date.now()) => ({
    alertId: alert.alertId,
    name: alert.name,
    ...(alert.description && { description: alert.description }),
    state: alertState(alert, now),
    ...(alert.snoozedUntil && alert.snoozedUntil > now && { snoozedUntil: isoTime(alert.snoozedUntil) }),
    emailRecipients: alert.recipients ?? [],
    slackChannels: alert.slackRecipients?.length ?? 0,
    ...(alert.lastFiredAt && { lastFiredAt: isoTime(alert.lastFiredAt) }),
    firedLast24Hours: alert.eventCount24h,
    firedTotal: alert.totalEventCount
});
const describeDelivery = ({ email, slack }) => [email && `email ${email}`, slack && `Slack ${slack}`].filter(Boolean).join(', ') || 'no notification';
export const describeAlert = (alert, events, now = Date.now()) => ({
    ...summarizeAlert(alert, now),
    conditions: alert.conditions,
    ...(alert.emailSubjectTemplate && { emailSubject: alert.emailSubjectTemplate }),
    recentEvents: events.slice(0, ALERT_EVENTS_SHOWN).map(({ callId, chatId, deliveryStatus, createdAt }) => ({
        firedAt: isoTime(createdAt),
        ...(callId && { conversationId: callId, kind: 'call' }),
        ...(chatId && !callId && { conversationId: chatId, kind: 'chat' }),
        delivery: describeDelivery(deliveryStatus)
    }))
});
/** The app's alert update body for an update_alert call. Only what the call sets is sent. */
export const toAlertUpdate = ({ name, description, conditions, emailRecipients, emailSubject, on, snoozeHours }, now = Date.now()) => ({
    ...(name !== undefined && { name }),
    ...(description !== undefined && { description }),
    ...(conditions !== undefined && { conditions }),
    ...(emailRecipients !== undefined && { recipients: emailRecipients }),
    ...(emailSubject !== undefined && { emailSubjectTemplate: emailSubject }),
    ...(on === true && { enabledAt: now, snoozedUntil: null }),
    ...(on === false && { enabledAt: null }),
    ...(snoozeHours !== undefined &&
        on !== true && { snoozedUntil: snoozeHours > 0 ? now + snoozeHours * HOUR_MS : null })
});
/** The app's alert create body for a create_alert call. */
export const toAlertCreate = ({ name, description, conditions, emailRecipients, emailSubject }) => ({
    name,
    ...(description !== undefined && { description }),
    conditions,
    recipients: emailRecipients,
    slackRecipients: [],
    ...(emailSubject !== undefined && { emailSubjectTemplate: emailSubject })
});
//# sourceMappingURL=alerts.js.map